export interface ProductSelectorRule {
  rule_id?: string;
  attribute: string;
  selector_key?: string | null;
  operator: string;
  values?: string[];
  effect_summary?: string | null;
}

export interface ProductSelector {
  mode: string;
  included_product_ids?: string[];
  included_variant_ids?: string[];
  rules?: ProductSelectorRule[];
}

const publicationScopeAliases: Record<string, string> = {
  public: 'public',
  owner_only: 'owner_only',
  organizations: 'shared_organizations',
  shared_organizations: 'shared_organizations',
  tenants: 'shared_tenants',
  shared_tenants: 'shared_tenants',
  stores: 'shared_stores',
  shared_stores: 'shared_stores',
  agents: 'agent_only',
  agent_only: 'agent_only',
};

export function canonicalizePublicationScope(scope: string | null | undefined): string {
  const normalized = `${scope ?? ''}`.trim().toLowerCase();
  if (!normalized) {
    return '';
  }
  return publicationScopeAliases[normalized] ?? normalized;
}

export interface CatalogPublicationSection {
  section_id: string;
  publication_id: string;
  section_key: string;
  name: string;
  description?: string | null;
  status: string;
  display_mode: string;
  selection_mode: string;
  sort_order: number;
  product_selector: ProductSelector;
  product_ids?: string[];
  placements: CatalogPublicationSectionPlacement[];
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CatalogPublicationSectionPlacement {
  placement_id: string;
  section_id: string;
  placement_kind: 'suggested';
  product_id?: string | null;
  variant_id?: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProductClassification {
  classification_id: string;
  product_id: string;
  catalog_owner_tenant_id: string;
  kind: string;
  classification_key: string;
  classification_value: string;
  sort_order: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CatalogPublication {
  publication_id: string;
  catalog_owner_tenant_id: string;
  catalog_id: string;
  storefront_key?: string | null;
  catalog_key?: string | null;
  scope: string;
  status: string;
  pricebook_id?: string | null;
  default_pricebook_name?: string | null;
  commission_plan_id?: string | null;
  default_commission_plan_name?: string | null;
  exposure_kind?: string | null;
  exposure_label?: string | null;
  product_selector?: ProductSelector;
  sections?: CatalogPublicationSection[];
  metadata?: Record<string, unknown>;
  organization_ids?: string[];
  selling_tenant_ids?: string[];
  store_ids?: string[];
  agent_user_ids?: string[];
  created_at: string;
  updated_at: string;
}


export type CatalogPublicationAccessKind = 'organizations' | 'tenants' | 'stores' | 'agents';

export interface CatalogPublicationAccessSet {
  publication_id: string;
  kind: CatalogPublicationAccessKind;
  ids: string[];
}

export interface CommerceChannel {
  channel_id: string;
  catalog_owner_tenant_id: string;
  channel_key: string;
  name: string;
  kind: string;
  status: string;
  metadata?: Record<string, unknown>;
  principal_count: number;
  publication_count: number;
  created_at: string;
  updated_at: string;
}

export interface CommerceChannelPrincipal {
  channel_id: string;
  principal_type: string;
  principal_id: string;
  created_at: string;
}

export interface CommerceChannelPublication {
  channel_id: string;
  publication_id: string;
  catalog_id: string;
  catalog_key: string;
  storefront_key?: string | null;
  binding_role: string;
  status: string;
  scope: string;
  priority: number;
  audience: 'any' | 'public' | 'agent';
  market_id?: string | null;
  market_group_id?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface StorefrontExperiencePrincipal {
  principal_type: string;
  principal_id: string;
}

export interface StorefrontExperienceReadinessIssue {
  code: string;
  severity: string;
  message: string;
  repair_hint?: string | null;
  resource_ids?: Record<string, string>;
  repair_action?: string | null;
}

export interface StorefrontExperienceReadiness {
  effective_status: string;
  ready: boolean;
  issues: StorefrontExperienceReadinessIssue[];
}

export interface StorefrontExperience {
  experience_id: string;
  catalog_owner_tenant_id: string;
  experience_key: string;
  name: string;
  segment: string;
  eligibility_mode: string;
  route_profile: string;
  runtime_family: string;
  status: string;
  storefront_key: string;
  catalog_id: string;
  catalog_key: string;
  publication_id?: string | null;
  channel_id?: string | null;
  default_pricebook_id?: string | null;
  default_pricebook_name?: string | null;
  principal_defaults: StorefrontExperiencePrincipal[];
  metadata?: Record<string, unknown>;
  readiness: StorefrontExperienceReadiness;
  created_at: string;
  updated_at: string;
}

export interface AdminEligibilityPreviewInput {
  publication_id: string;
  channel_id: string;
  lease_context?: CheckoutLeaseContext | null;
  eligibility_mode?: string;
  route_profile?: string;
  selling_tenant_id?: string;
  store_id?: string;
  agent_user_id?: string;
  market_id?: string;
  google_place_id?: string;
  lat?: number;
  lng?: number;
  address?: string;
  segment?: string;
  business?: boolean;
  include_drafts?: boolean;
  place_data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewSection {
  section_id: string;
  section_key: string;
  name: string;
  description?: string | null;
  sellable_product_ids: string[];
  blocked_product_ids: string[];
}

export interface AdminEligibilityPreviewDiagnostic {
  severity: string;
  kind: string;
  code: string;
  message: string;
  product_id?: string | null;
  variant_id?: string | null;
  policy_id?: string | null;
  missing_fields?: string[];
  metadata?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewResolvedAddress {
  address?: string | null;
  google_place_id?: string | null;
  lat?: number | null;
  lng?: number | null;
  place_data?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewResolvedZone {
  zone_id: string;
  zone_key: string;
  name: string;
  zone_kind: string;
  source_system?: string | null;
  source_ref?: string | null;
  metadata?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewResolvedMarket {
  market_id: string;
  market_key: string;
  name: string;
  status: string;
  zone_id: string;
  zone_key: string;
  zone_name: string;
  zone_kind: string;
  can_sign_up: boolean;
  can_schedule_install: boolean;
  install_method?: string | null;
  customer_message?: string | null;
  market_group_id?: string | null;
  market_group_label?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
  metadata?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewResolvedPublication {
  publication_id: string;
  catalog_id: string;
  catalog_key?: string | null;
  catalog_owner_tenant_id: string;
  storefront_key?: string | null;
  scope: string;
  status: string;
  sections: CatalogPublicationSection[];
}

export interface AdminEligibilityPreviewResolvedChannel {
  channel_id: string;
  channel_key: string;
  name: string;
  kind: string;
  status: string;
  binding_status: string;
  metadata?: Record<string, unknown>;
}

export interface AdminEligibilityPreviewCommercialContext {
  catalog_owner_tenant_id: string;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  agent_user_id?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
}

export interface AdminEligibilityPreviewRouteEndpoint {
  id?: string | null;
  name?: string | null;
  latitude: number;
  longitude: number;
}

export interface AdminEligibilityPreviewRouteDistanceSignal {
  status: string;
  reason_code?: string | null;
  build_type?: string | null;
  origin?: AdminEligibilityPreviewRouteEndpoint | null;
  origin_approach_distance_ft?: number | null;
  origin_approach_distance_m?: number | null;
  road_distance_ft?: number | null;
  road_distance_m?: number | null;
  destination_approach_distance_ft?: number | null;
  destination_approach_distance_m?: number | null;
  total_build_distance_ft?: number | null;
  total_build_distance_m?: number | null;
  nearest_fiber_access_point_id?: string | null;
  nearest_fiber_access_point?: AdminEligibilityPreviewRouteEndpoint | null;
  access_point_layer_id?: string | null;
  access_point_layer_purpose?: string | null;
  access_point_layer_version?: number | null;
  distance_quality?: string | null;
  source?: string | null;
  routing_profile?: string | null;
  route?: Record<string, unknown> | null;
}

export interface AdminEligibilityPreviewPreliminaryEstimate {
  estimate_status: string;
  quote_required: boolean;
  reason_code?: string | null;
  build_type?: string | null;
  origin?: AdminEligibilityPreviewRouteEndpoint | null;
  origin_approach_distance_ft?: number | null;
  origin_approach_distance_m?: number | null;
  road_distance_ft?: number | null;
  road_distance_m?: number | null;
  destination_approach_distance_ft?: number | null;
  destination_approach_distance_m?: number | null;
  total_build_distance_ft?: number | null;
  total_build_distance_m?: number | null;
  nearest_fiber_access_point_id?: string | null;
  nearest_fiber_access_point?: AdminEligibilityPreviewRouteEndpoint | null;
  access_point_layer_id?: string | null;
  access_point_layer_version?: number | null;
  distance_quality?: string | null;
  source?: string | null;
  routing_profile?: string | null;
  route?: Record<string, unknown> | null;
  base_price_per_meter?: number | null;
  price_per_meter?: number | null;
  price_source?: string | null;
  estimated_quote_amount?: number | null;
  currency?: string | null;
  disclaimer?: string | null;
}

export interface AdminEligibilityPreviewProductVariant {
  variant_id: string;
  variant_key: string;
  display_name: string;
  reasons: string[];
  policy_ids?: string[];
}

export interface AdminEligibilityPreviewProductBlock {
  product_id: string;
  product_type_id?: string;
  name: string;
  display_name: string;
  description?: string;
  class?: string;
  reasons: string[];
  policy_ids?: string[];
  blocked_variants: AdminEligibilityPreviewProductVariant[];
}

export interface CheckoutCharge {
  charge_type: string;
  amount: number;
  quantity: number;
  currency?: string;
  billing_period_months?: number;
  timing?: string;
  applies_at?: string;
  source_type?: string;
  source_id?: string;
  label?: string;
}

export interface CheckoutMediaAsset {
  name?: string;
  media_type?: string;
  media_url?: string;
  image_position?: string;
  image_size?: string;
  image_height?: string;
  image_width?: string;
}

export interface CheckoutFeature {
  icon?: string;
  description: string;
  icon_url?: string;
}

export interface CheckoutFee {
  name: string;
  type?: string;
  amount: number;
  percentage: number;
  is_percent: boolean;
  currency?: string;
  recurring: boolean;
  charge_type?: string;
  billing_period_months?: number;
  quantity?: number;
  timing?: string;
  applies_at?: string;
  optional: boolean;
  description?: string;
  metadata?: Record<string, unknown>;
}

export interface CheckoutAppliedDiscount {
  discount_id: string;
  discount_code_id: string;
  discount_code: string;
  name: string;
  type?: string;
  applies_to?: string;
  amount: number;
  percentage: number;
  is_percent: boolean;
  discount_length: number;
  charge_type?: string;
  timing?: string;
  message?: string;
  metadata?: Record<string, unknown>;
}

export interface OfferLeaseTerms {
  purchase_cost_amount_minor?: number | null;
  purchase_cost_currency?: string | null;
  lease_rate_bps?: number | null;
  allowed_term_months?: number[];
  minimum_upfront_payment_amount_minor?: number | null;
  maximum_upfront_payment_amount_minor?: number | null;
}

export interface CheckoutLeaseContext {
  selected_term_months: number;
}

export interface CheckoutLeaseSelection {
  selected_upfront_payment_amount_minor: number;
}

export interface CheckoutLeasePricingSnapshot {
  calculation_version: string;
  rounding_mode: string;
  selected_term_months: number;
  selected_upfront_payment_amount_minor: number;
  final_monthly_amount_minor: number;
}

export interface CheckoutFreePeriod {
  promotion_id: string;
  benefit_id: string;
  discount_code_id?: string;
  label: string;
  line_item_index: number;
  variant_id: string;
  offer_id?: string;
  billing_period_months: number;
  application_policy: 'overlap' | 'extend_free_periods';
  start_billing_cycle: number;
  duration_billing_cycles: number;
}

export interface CheckoutRecurringPhase {
  amount_minor: number;
  billing_period_months: number;
  duration_billing_cycles?: number;
  discount_ids: string[];
  labels: string[];
}

export interface CheckoutAvailablePricingPreview {
  status: 'preview';
  base_recurring_amount_minor: number;
  lowest_recurring_amount_minor: number;
  currency: string;
  recurring_phases: CheckoutRecurringPhase[];
  free_periods: CheckoutFreePeriod[];
  applied_discounts: Array<{ discount_id: string; label: string; discount_type: string; recurring_delta: number; discount_length: number; starts_at?: string; ends_at?: string }>;
  required_selections: Array<{ key: string; value: boolean; label: string; disclosure?: string }>;
}

export interface CheckoutOffer {
  available_pricing_preview?: CheckoutAvailablePricingPreview;
  offer_id: string;
  variant_id: string;
  offer_key: string;
  name: string;
  display_name: string;
  offer_type: string;
  is_default: boolean;
  sellable: boolean;
  requires_lease_selection?: boolean;
  lease_terms?: OfferLeaseTerms | null;
  fulfillment_code?: string;
  price: number;
  discounted_price: number;
  discount_length: number;
  startup_fee: number;
  min_price: number;
  max_price: number;
  currency: string;
  charge_type?: string;
  billing_period_months?: number;
  timing?: string;
  metadata?: Record<string, unknown>;
  charges?: CheckoutCharge[];
  discounts?: CheckoutAppliedDiscount[];
}

export interface AdminEligibilityPreviewProduct {
  product_id: string;
  product_type_id?: string;
  name: string;
  display_name: string;
  description?: string;
  class?: string;
  visible: boolean;
  agents_only: boolean;
  requires_bundle: boolean;
  limit_per_cart: number;
  metadata?: Record<string, unknown>;
  facets?: ProductClassification[];
  variants: AdminEligibilityPreviewSellableVariant[];
}

export interface AdminEligibilityPreviewSellableVariant {
  product_id: string;
  variant_id: string;
  offer_id?: string;
  bundle_id?: string;
  can_remove: boolean;
  locked_reason?: string;
  required_by_variant_id?: string;
  product_key: string;
  variant_key: string;
  offer_key?: string;
  product_name: string;
  display_name: string;
  offer_name?: string;
  offer_type?: string;
  description?: string;
  service_class?: string;
  unique_plan_identifier?: string;
  market_id?: string | null;
  quantity: number;
  price: number;
  discounted_price: number;
  discount_length: number;
  startup_fee: number;
  min_price: number;
  max_price: number;
  currency: string;
  charge_type?: string;
  billing_period_months?: number;
  timing?: string;
  class?: string;
  plan_code?: string;
  limit_per_cart: number;
  installation_required: boolean;
  metadata?: Record<string, unknown>;
  offers?: CheckoutOffer[];
  media: CheckoutMediaAsset[];
  specifications: VariantSpecification[];
  features: CheckoutFeature[];
  fees: CheckoutFee[];
  discounts: CheckoutAppliedDiscount[] | null;
  charges?: CheckoutCharge[];
  allow_source: string;
  allow_reasons: string[];
  allow_policy_ids?: string[];
  lease_context?: CheckoutLeaseContext | null;
  selected_term_months?: number | null;
  selected_upfront_payment_amount_minor?: number | null;
  lease_selection?: CheckoutLeaseSelection | null;
  lease_pricing_snapshot?: CheckoutLeasePricingSnapshot | null;
}

export interface AdminEligibilityPreviewResponse {
  status: string;
  quote_required: boolean;
  quote_request_reason_code?: string | null;
  customer_message?: string | null;
  lease_context?: CheckoutLeaseContext | null;
  input_summary: {
    catalog_owner_tenant_id: string;
    publication_id: string;
    channel_id: string;
    lease_context?: CheckoutLeaseContext | null;
    eligibility_mode: string;
    route_profile?: string | null;
    selling_tenant_id?: string | null;
    store_id?: string | null;
    agent_user_id?: string | null;
    market_id?: string | null;
    google_place_id?: string | null;
    address?: string | null;
    segment?: string | null;
    business: boolean;
    include_drafts: boolean;
    metadata?: Record<string, unknown>;
  };
  resolved_address: AdminEligibilityPreviewResolvedAddress;
  resolved_eligibility_mode: string;
  resolved_route_profile?: string | null;
  resolved_zone?: AdminEligibilityPreviewResolvedZone | null;
  resolved_market?: AdminEligibilityPreviewResolvedMarket | null;
  route_distance_signal?: AdminEligibilityPreviewRouteDistanceSignal | null;
  preliminary_estimate?: AdminEligibilityPreviewPreliminaryEstimate | null;
  resolved_publication: AdminEligibilityPreviewResolvedPublication;
  resolved_channel: AdminEligibilityPreviewResolvedChannel;
  resolved_commercial_context: AdminEligibilityPreviewCommercialContext;
  sections: AdminEligibilityPreviewSection[];
  sellable_products: AdminEligibilityPreviewProduct[];
  blocked_products: AdminEligibilityPreviewProductBlock[];
  diagnostics: AdminEligibilityPreviewDiagnostic[];
  warnings: string[];
}

export interface Pricebook {
  pricebook_id: string;
  catalog_owner_tenant_id: string;
  catalog_id?: string | null;
  selling_tenant_id?: string | null;
  name: string;
  status: string;
  currency?: string | null;
  pricing_strategy?: string | null;
  lease_terms?: {
    allowed_term_months?: number[] | null;
  } | null;
  quote_calculation_policy?: QuoteCalculationPolicy | null;
  readiness_label?: string | null;
  pricing_snapshot?: Record<string, unknown>;
  offer_prices?: Array<{
    row_id: string;
    offer_id: string;
    variant_id: string;
    product_label: string;
    variant_label: string;
    offer_label: string;
    offer_type: string;
    term_months?: number | null;
  billing_period_months?: number | null;
    charge_type: string;
    amount?: string | null;
    percentage?: string | null;
    currency?: string | null;
    lease_pricing?: {
      purchase_cost_amount_minor?: number | null;
      purchase_cost_currency?: string | null;
      intended_sale_price_amount_minor?: number | null;
      intended_sale_price_currency?: string | null;
      lease_rate_bps?: number | null;
      minimum_upfront_payment_amount_minor?: number | null;
      maximum_upfront_payment_amount_minor?: number | null;
    } | null;
    fees?: Array<{
      row_id: string;
      fee_label: string;
      fee_code?: string | null;
      charge_type: string;
      amount?: string | null;
      percentage?: string | null;
      currency?: string | null;
      billing_period?: string | null;
      starts_at?: string | null;
      ends_at?: string | null;
      summary: string;
    }> | null;
    starts_at?: string | null;
    ends_at?: string | null;
    summary: string;
  }>;
  variant_prices?: Array<{
    row_id: string;
    variant_id: string;
    product_label: string;
    variant_label: string;
    charge_type: string;
    amount?: string | null;
    percentage?: string | null;
    currency?: string | null;
    billing_period?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    summary: string;
  }>;
  fee_prices?: Array<{
    row_id: string;
    fee_id: string;
    fee_label: string;
    applies_to_label: string;
    charge_type: string;
    amount?: string | null;
    percentage?: string | null;
    currency?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    summary: string;
  }>;
  offer_targets?: Array<{
    offer_id: string;
    variant_id: string;
    product_id: string;
    product_label: string;
    variant_label: string;
    offer_label: string;
    offer_key: string;
    offer_type: string;
    status: string;
    sellable: boolean;
    is_default: boolean;
    term_months?: number | null;
  billing_period_months?: number | null;
  }>;
  variant_targets?: Array<{
    variant_id: string;
    product_id: string;
    product_label: string;
    variant_label: string;
    variant_key: string;
    status: string;
    sellable: boolean;
  }>;
  fee_targets?: Array<{
    fee_id: string;
    variant_id: string;
    fee_label: string;
    applies_to_label: string;
    fee_type?: string | null;
    charge_timing: string;
    required: boolean;
  }>;
  coverage?: {
    variant_coverage_label: string;
    fee_coverage_label: string;
    missing_rows_label?: string | null;
    override_scope_label?: string | null;
  };
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type ConstructionPricingMode = 'not_required' | 'commerce_rate' | 'external_cost';

export type ConstructionEstimatorAuthorization = 'any_authorized' | 'selected';

export type QuoteCalculationChargeType =
  | 'recurring'
  | 'one_time'
  | 'installation'
  | 'construction'
  | 'deposit'
  | 'usage';

export interface QuoteCalculationTaxRule {
  rule_id: string;
  name: string;
  rate_bps: number;
  categories: string[];
  charge_types?: QuoteCalculationChargeType[];
}

export interface QuoteCalculationPolicy {
  construction_pricing_mode?: ConstructionPricingMode;
  construction_customer_markup_bps?: number;
  construction_estimator_authorization: ConstructionEstimatorAuthorization;
  construction_estimator_service_account_ids?: string[];
  tax_rules?: QuoteCalculationTaxRule[];
  construction_taxable?: boolean;
  construction_tax_category?: string;
}

export interface PricebookAssignment {
  assignment_id: string;
  pricebook_id: string;
  pricebook_name?: string | null;
  catalog_owner_tenant_id: string;
  publication_id?: string | null;
  publication_label?: string | null;
  organization_id?: string | null;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  agent_user_id?: string | null;
  target_kind?: string | null;
  target_label?: string | null;
  target_summary?: string | null;
  validity_label?: string | null;
  status: string;
  starts_at?: string | null;
  ends_at?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CommissionPlanAssignment {
  assignment_id: string;
  commission_plan_id: string;
  commission_plan_name?: string | null;
  catalog_owner_tenant_id: string;
  publication_id?: string | null;
  publication_label?: string | null;
  organization_id?: string | null;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  agent_user_id?: string | null;
  target_kind?: string | null;
  target_label?: string | null;
  target_summary?: string | null;
  validity_label?: string | null;
  status: string;
  starts_at?: string | null;
  ends_at?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Catalog {
  catalog_id: string;
  catalog_owner_tenant_id: string;
  catalog_key: string;
  name: string;
  status: string;
  default_currency?: string | null;
  description?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ProductOptionValue {
  option_value_id: string;
  option_id: string;
  value: string;
  display_value?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
}

export interface ProductOption {
  option_id: string;
  product_id: string;
  name: string;
  display_name?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
  values: ProductOptionValue[];
}

export interface ProductMediaPlacement {
  placement_id: string;
  product_id: string;
  variant_id?: string | null;
  role: string;
  alt_text?: string | null;
  caption?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
  asset: {
    asset_id: string;
    storage_provider?: string | null;
    storage_key?: string | null;
    media_url?: string | null;
    media_url_expires_at?: string | null;
    mime_type?: string | null;
    width?: number | null;
    height?: number | null;
    file_size_bytes?: number | null;
    checksum?: string | null;
    metadata?: Record<string, unknown>;
  };
}

export interface ProductMediaUploadInitRequest {
  filename: string;
  mime_type: string;
  file_size_bytes: number;
}

export interface ProductMediaUploadInitResponse {
  asset_id: string;
  storage_provider: string;
  storage_key: string;
  presigned_url: string;
  expires_at: string;
}

export interface ProductMediaUploadCompleteRequest {
  asset_id: string;
  storage_provider?: string;
  storage_key: string;
  mime_type: string;
  file_size_bytes: number;
  role?: string;
  alt_text?: string;
  caption?: string;
  sort_order?: number;
  variant_id?: string;
  metadata?: Record<string, unknown>;
}

export interface ProductMediaUploadProgress {
  loadedBytes: number;
  totalBytes: number;
}

export interface ProductMediaUploadOptions {
  signal?: AbortSignal;
  onProgress?: (progress: ProductMediaUploadProgress) => void;
}

export interface VariantFeature {
  icon?: string | null;
  feature_id: string;
  variant_id: string;
  description: string;
  icon_url?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
}

export interface VariantFee {
  fee_id: string;
  variant_id: string;
  fee_key: string;
  name: string;
  fee_type?: string | null;
  charge_timing: string;
  required: boolean;
  display_text?: string | null;
  metadata?: Record<string, unknown>;
}

export interface VariantRecommendation {
  recommendation_id: string;
  variant_id: string;
  recommended_product_id?: string | null;
  recommended_variant_id?: string | null;
  reason?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
}

export interface VariantBundleComponent {
  bundle_component_id: string;
  variant_id: string;
  component_product_id?: string | null;
  component_variant_id?: string | null;
  required: boolean;
  default_quantity: number;
  bundle_price_mode: 'standard' | 'included_free' | 'fixed_price' | 'amount_off' | 'percent_off';
  bundle_price_amount_minor?: number | null;
  bundle_price_percentage?: number | null;
  metadata?: Record<string, unknown>;
}

export interface ProductVariantOffer {
  offer_id: string;
  variant_id: string;
  product_id: string;
  catalog_owner_tenant_id: string;
  offer_key: string;
  name: string;
  display_name?: string | null;
  offer_type: string;
  status: string;
  sellable: boolean;
  is_default: boolean;
  term_months?: number | null;
  billing_period_months?: number | null;
  purchase_cost_amount_minor?: number | null;
  purchase_cost_currency?: string | null;
  lease_rate_bps?: number | null;
  allowed_term_months?: number[];
  minimum_upfront_payment_amount_minor?: number | null;
  maximum_upfront_payment_amount_minor?: number | null;
  lease_terms?: OfferLeaseTerms | null;
  fulfillment_code?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type CommerceAttributeValueType = 'integer' | 'decimal' | 'text' | 'boolean' | 'object';

export interface CommerceAttributeDefinition {
  attribute_definition_id: string;
  attribute_key: string;
  display_name: string;
  value_type: CommerceAttributeValueType;
  quantity_dimension?: string | null;
  canonical_unit?: string | null;
  public_visibility: boolean;
  validation_rules: Record<string, unknown>;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ProductVariantAttributeValue {
  variant_id: string;
  attribute_definition_id: string;
  attribute_key: string;
  value_type: CommerceAttributeValueType;
  quantity_dimension?: string | null;
  canonical_unit?: string | null;
  public_visibility: boolean;
  integer_value?: number | null;
  decimal_value?: number | null;
  text_value?: string | null;
  boolean_value?: boolean | null;
  object_value?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface VariantSpecification {
  group?: string;
  name: string;
  value: string;
}

export interface ProductVariant {
  specifications: VariantSpecification[];
  variant_id: string;
  product_id: string;
  catalog_owner_tenant_id: string;
  variant_key: string;
  name: string;
  display_name?: string | null;
  status: string;
  sellable: boolean;
  installation_required: boolean;
  fulfillment_code?: string | null;
  inventory_policy?: string | null;
  sort_order: number;
  metadata?: Record<string, unknown>;
  attributes: ProductVariantAttributeValue[];
  option_value_ids: string[];
  option_values: ProductOptionValue[];
  offers?: ProductVariantOffer[];
  features: VariantFeature[];
  fee_definitions: VariantFee[];
  recommendations: VariantRecommendation[];
  bundle_components: VariantBundleComponent[];
  created_at: string;
  updated_at: string;
}

export interface Product {
  product_id: string;
  catalog_id: string;
  catalog_owner_tenant_id: string;
  product_key: string;
  name: string;
  short_description?: string | null;
  description?: string | null;
  status: string;
  publish_status: string;
  browse_visibility: string;
  product_type_key?: string | null;
  service_class?: string | null;
  service_group?: string | null;
  default_variant_id?: string | null;
  sort_order: number;
  facets?: ProductClassification[];
  flags?: ProductClassification[];
  metadata?: Record<string, unknown>;
  variants: ProductVariant[];
  options: ProductOption[];
  media: ProductMediaPlacement[];
  created_at: string;
  updated_at: string;
}

export interface TimelineNote {
  note_id: string;
  author_user_id?: string | null;
  body: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TimelineEvent {
  event_id: string;
  event_type: string;
  actor_user_id?: string | null;
  payload?: Record<string, unknown>;
  created_at: string;
}

export interface Quote {
  quote_id: string;
  friendly_quote_id: string;
  quote_kind?: string | null;
  workflow_stage?: string | null;
  tenant_id: string;
  catalog_owner_tenant_id: string;
  selling_tenant_id: string;
  store_id?: string | null;
  agent_user_id?: string | null;
  status: string;
  commercial_state?: string | null;
  calculation_status?: string | null;
  quote_origin?: string | null;
  customer_snapshot?: Record<string, unknown>;
  service_address_snapshot?: Record<string, unknown>;
  line_items_draft?: QuoteLineItem[];
  pricing_snapshot?: Record<string, unknown>;
  pricing_revision?: number | null;
  construction_cost_snapshot?: Record<string, unknown>;
  customer_price_snapshot?: Record<string, unknown>;
  discounts_snapshot?: Record<string, unknown>;
  eligibility_snapshot?: Record<string, unknown>;
  request_snapshot?: Record<string, unknown>;
  approval_snapshot?: Record<string, unknown>;
  seller_revision_request_snapshot?: Record<string, unknown>;
  customer_acceptance_snapshot?: Record<string, unknown>;
  customer_document_snapshot?: Record<string, unknown>;
  publication_id?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
  valid_until?: string | null;
  converted_order_id?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  submitted_at?: string | null;
  approved_at?: string | null;
  converted_at?: string | null;
}

export interface QuoteLineItem {
  product_id?: string;
  variant_id?: string;
  offer_id?: string;
  product_name?: string;
  display_name?: string;
  name?: string;
  sku?: string;
  quantity?: number;
  price?: number;
  discounted_price?: number;
  currency?: string;
  charge_type?: string;
  billing_period_months?: number;
  frequency?: string;
  timing?: string;
  charges?: CheckoutCharge[];
}

export type QuoteWorkspace = 'catalog-owner' | 'seller';

export interface OrderAction {
  action_id: string;
  label: string;
  category: string;
  state: string;
  target_status?: string | null;
  target_lifecycle_phase?: string | null;
  blocked_reasons: string[];
  completed_at?: string | null;
}

type OrderActionResponse = Omit<OrderAction, 'blocked_reasons'> & {
  blocked_reasons?: string[] | null;
};

export interface CommissionPlan {
  commission_plan_id: string;
  catalog_owner_tenant_id: string;
  selling_tenant_id?: string | null;
  name: string;
  status: string;
  currency?: string | null;
  rules_snapshot?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface EligibilityPolicy {
  policy_id: string;
  policy_label?: string | null;
  catalog_owner_tenant_id: string;
  publication_id?: string | null;
  publication_label?: string | null;
  subject_type: string;
  subject_id?: string | null;
  subject_key?: string | null;
  market_id?: string | null;
  market_group_id?: string | null;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  agent_user_id?: string | null;
  action: string;
  requires_can_sign_up: boolean;
  requires_can_schedule_install: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  priority: number;
  effective_status?: string | null;
  scope_summary?: string | null;
  requirement_summary?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Market {
  market_id: string;
  catalog_owner_tenant_id: string;
  zone_id: string;
  market_key: string;
  name: string;
  status: string;
  install_method?: string | null;
  can_sign_up: boolean;
  can_schedule_install: boolean;
  estimated_ready_at?: string | null;
  customer_message?: string | null;
  market_group_id?: string | null;
  market_group_label?: string | null;
  eligibility_policy_count: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ServiceabilityZone {
  zone_id: string;
  catalog_owner_tenant_id?: string | null;
  zone_key: string;
  name: string;
  zone_kind: string;
  source_system?: string | null;
  source_ref?: string | null;
  market_ids: string[];
  market_labels: string[];
  market_count: number;
  build_type_rates?: RoutePricingBuildTypeRate[];
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface RoutePricingBuildTypeRate {
  build_type: string;
  currency: string;
  price_per_meter: string;
  minimum_build_cost?: string | null;
}

export interface AccessPointLayer {
  layer_id: string;
  catalog_owner_tenant_id: string;
  layer_key: string;
  name: string;
  access_point_type: string;
  purpose: string;
  status: string;
  active_version: number;
  mapping_source_id?: string | null;
  point_count: number;
  default_build_type_rates?: RoutePricingBuildTypeRate[];
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AccessPoint {
  access_point_id: string;
  layer_id: string;
  external_ref?: string | null;
  name?: string | null;
  latitude: number;
  longitude: number;
  enabled: boolean;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AccessPointImport {
  import_id: string;
  layer_id: string;
  catalog_owner_tenant_id: string;
  source_name?: string | null;
  source_format: string;
  status: string;
  point_count: number;
  error_count: number;
  error_snapshot: unknown[];
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface ReplaceAccessPointsResult {
  layer: AccessPointLayer;
  import: AccessPointImport;
  point_count: number;
  access_points: AccessPoint[];
}


export interface MarketGroup {
  market_group_id: string;
  catalog_owner_tenant_id: string;
  market_group_key: string;
  name: string;
  market_ids: string[];
  market_labels: string[];
  market_count: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PromotionCondition {
  condition_id?: string;
  promotion_id?: string;
  condition_type: string;
  operator: string;
  value: unknown;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface PromotionEligibilityScope {
  scope_id?: string;
  promotion_id?: string;
  scope_type: string;
  scope_value?: string | null;
  exclude?: boolean;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface PromotionAutoPayConfiguration {
  label: string;
  description: string;
  required: boolean;
  true_label: string;
  false_label: string;
  disclosure?: string;
  enrollment_status: 'pending';
  deferred_enrollment_copy?: string;
}

export interface PromotionBenefit {
  benefit_id?: string;
  promotion_id?: string;
  benefit_type: string;
  application_policy?: string;
  applies_to: string;
  currency?: string | null;
  amount_minor?: number | null;
  percentage?: number | null;
  max_discount_minor?: number | null;
  discount_length?: number | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface PromotionCode {
  code_id?: string;
  promotion_id?: string;
  code: string;
  status: string;
  starts_at?: string | null;
  ends_at?: string | null;
  use_limit?: number | null;
  used_count?: number;
  metadata?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface AdminResellerEntitlementGrant {
  kind: string;
  id?: string | null;
}

export interface AdminResellerEntitlement {
  entitlement_id: string;
  publication_id: string;
  catalog_owner_tenant_id: string;
  catalog_id: string;
  catalog_key?: string | null;
  storefront_key?: string | null;
  publication_scope: string;
  publication_status: string;
  name: string;
  description?: string | null;
  status: string;
  default_pricebook_id?: string | null;
  default_commission_plan_id?: string | null;
  metadata?: Record<string, unknown>;
  organization_ids: string[];
  selling_tenant_ids: string[];
  store_ids: string[];
  agent_user_ids: string[];
  grants: AdminResellerEntitlementGrant[];
  created_at: string;
  updated_at: string;
}

export interface AdminResellerEntitlementInput {
  catalog_owner_tenant_id: string;
  publication_id: string;
  default_commission_plan_id?: string | null;
  name: string;
  description?: string | null;
  status: string;
  metadata?: Record<string, unknown>;
  organization_ids: string[];
  selling_tenant_ids: string[];
  store_ids: string[];
  agent_user_ids: string[];
}

export interface UpdateAdminResellerEntitlementInput {
  publication_id?: string;
  default_commission_plan_id?: string | null;
  name?: string;
  description?: string | null;
  status?: string;
  metadata?: Record<string, unknown>;
  organization_ids?: string[];
  selling_tenant_ids?: string[];
  store_ids?: string[];
  agent_user_ids?: string[];
}

export interface ResellerEntitlementAccess {
  kind: string;
  id?: string | null;
}

export interface ResellerEntitlement {
  entitlement_id: string;
  publication_id: string;
  catalog_owner_tenant_id: string;
  catalog_id: string;
  catalog_key?: string | null;
  storefront_key?: string | null;
  publication_scope: string;
  publication_status: string;
  name: string;
  description?: string | null;
  status: string;
  selling_tenant_id: string;
  store_id?: string | null;
  agent_user_id?: string | null;
  organization_ids: string[];
  access: ResellerEntitlementAccess;
  default_pricebook_id?: string | null;
  default_commission_plan_id?: string | null;
  resolved_pricebook_id?: string | null;
  resolved_commission_plan_id?: string | null;
}

export interface CreateSalesQuoteInput {
  tenant_id: string;
  catalog_owner_tenant_id?: string | null;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  agent_user_id?: string | null;
  status?: string;
  customer_snapshot?: Record<string, unknown>;
  service_address_snapshot?: Record<string, unknown>;
  line_items_draft?: Array<Record<string, unknown>>;
  pricing_snapshot?: Record<string, unknown>;
  discounts_snapshot?: Record<string, unknown>;
  eligibility_snapshot?: Record<string, unknown>;
  approval_snapshot?: Record<string, unknown>;
  publication_id?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
  valid_until?: string | null;
  metadata?: Record<string, unknown>;
}

export interface CreateSalesOrderInput {
  tenant_id: string;
  catalog_owner_tenant_id?: string | null;
  selling_tenant_id?: string | null;
  store_id?: string | null;
  submitted_by_user_id?: string | null;
  submitted_by_email?: string | null;
  assigned_agent_user_id?: string | null;
  status?: string;
  lifecycle_phase?: string;
  customer_snapshot?: Record<string, unknown>;
  service_address_snapshot?: Record<string, unknown>;
  billing_address_snapshot?: Record<string, unknown>;
  line_items_snapshot?: Array<Record<string, unknown>>;
  pricing_snapshot?: Record<string, unknown>;
  discounts_snapshot?: Record<string, unknown>;
  eligibility_snapshot?: Record<string, unknown>;
  publication_id?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
  commission_snapshot?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

export interface CommissionLedgerLine {
  commission_line_id: string;
  order_line_id?: string | null;
  line_type: string;
  calculation_snapshot?: Record<string, unknown>;
  amount: string;
  currency: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface CommissionLedgerEntry {
  commission_entry_id: string;
  order_id: string;
  friendly_order_id: string;
  catalog_owner_tenant_id: string;
  selling_tenant_id: string;
  store_id?: string | null;
  agent_user_id?: string | null;
  commission_plan_id?: string | null;
  status: string;
  paid?: boolean;
  calculation_snapshot?: Record<string, unknown>;
  amount: string;
  currency: string;
  earned_at?: string | null;
  approved_at?: string | null;
  paid_at?: string | null;
  reversed_at?: string | null;
  created_at: string;
  updated_at: string;
  lines?: CommissionLedgerLine[];
  audit_trail?: unknown[];
}

export interface CommissionStatusAmount {
  currency: string;
  amount: string;
}

export interface ResellerCommissionSummary {
  selling_tenant_id: string;
  order_count: number;
  latest_order_at?: string | null;
  commission_entry_count: number;
  latest_commission_activity_at?: string | null;
  commission_totals_by_status: Record<string, CommissionStatusAmount[]>;
  commission_counts_by_status: Record<string, number>;
}

export interface Promotion {
  promotion_id: string;
  catalog_owner_tenant_id?: string;
  promotion_key?: string;
  name: string;
  description?: string | null;
  status: string;
  discount_type: 'standard' | 'conditional' | 'context' | 'code_required';
  redemption_method: 'automatic' | 'customer_code' | 'hidden_code';
  conditions?: PromotionCondition[];
  eligibility_scopes?: PromotionEligibilityScope[];
  autopay_configuration?: PromotionAutoPayConfiguration | null;
  starts_at?: string | null;
  ends_at?: string | null;
  stack_policy?: string;
  stack_group?: string | null;
  priority?: number;
  display?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  benefits?: PromotionBenefit[];
  codes?: PromotionCode[];
  created_at: string;
  updated_at: string;
}

export interface Cart {
  cart_id: string;
  catalog_owner_tenant_id?: string | null;
  selling_tenant_id?: string | null;
  status: string;
  pricing_snapshot?: Record<string, unknown>;
  service_address_snapshot?: Record<string, unknown>;
  customer_snapshot?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ListPaginationInput {
  limit?: number;
  offset?: number;
}

export interface CommerceRegistryListInput extends ListPaginationInput {
  search?: string;
  status?: string[];
  purpose?: string[];
  sort?: string[];
  include_facets?: string[];
}

export interface CommerceFacetBucket {
  value: string;
  label?: string | null;
  count: number;
}

export interface PaginatedListResponse<T> {
  items?: T[] | null;
  total_count?: number | null;
  limit?: number | null;
  offset?: number | null;
  facets?: Record<string, CommerceFacetBucket[] | null> | null;
}

export interface PaginatedList<T> {
  items: T[];
  total_count: number;
  limit: number;
  offset: number;
  facets: Record<string, CommerceFacetBucket[]>;
}

type CatalogOwnerTenantFilter = {
  catalogOwnerTenantId: string;
};

type CatalogListFilters = CatalogOwnerTenantFilter & CommerceRegistryListInput;

function catalogOwnerTenantQuery(catalogOwnerTenantId: string): string {
  return encodeQuery({ catalog_owner_tenant_id: catalogOwnerTenantId });
}

type CatalogOwnerTenantListFilters = CatalogOwnerTenantFilter &
  Record<string, string | number | boolean | readonly string[] | undefined | null>;

function catalogOwnerTenantFilterQuery({
  catalogOwnerTenantId,
  ...filters
}: CatalogOwnerTenantListFilters): string {
  return encodeQuery({
    catalog_owner_tenant_id: catalogOwnerTenantId,
    ...filters,
  });
}

export async function listCommissionPlans(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput,
): Promise<PaginatedList<CommissionPlan>> {
  const response = await requestJSON<PaginatedListResponse<CommissionPlan> | null>(
    `/api/v1/commerce/admin/commission-plans${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function createCommissionPlan(payload: Record<string, unknown>): Promise<CommissionPlan> {
  return requestJSON<CommissionPlan>('/api/v1/commerce/admin/commission-plans', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateCommissionPlan(planId: string, payload: Record<string, unknown>): Promise<CommissionPlan> {
  return requestJSON<CommissionPlan>(`/api/v1/commerce/admin/commission-plans/${encodeURIComponent(planId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteCommissionPlan(planId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/commission-plans/${encodeURIComponent(planId)}`, {
    method: 'DELETE',
  });
}

export async function listEligibilityPolicies(
  filters: CommerceRegistryListInput & {
    publication_id?: string;
    action?: string[];
    subject_type?: string[];
  } = {},
): Promise<PaginatedList<EligibilityPolicy>> {
  const response = await requestJSON<PaginatedListResponse<EligibilityPolicy> | null>(
    `/api/v1/commerce/admin/eligibility-policies${encodeQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getEligibilityPolicy(policyId: string): Promise<EligibilityPolicy> {
  return requestJSON<EligibilityPolicy>(
    `/api/v1/commerce/admin/eligibility-policies/${encodeURIComponent(policyId)}`
  );
}

export async function createEligibilityPolicy(payload: Record<string, unknown>): Promise<EligibilityPolicy> {
  return requestJSON<EligibilityPolicy>('/api/v1/commerce/admin/eligibility-policies', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateEligibilityPolicy(
  policyId: string,
  payload: Record<string, unknown>
): Promise<EligibilityPolicy> {
  return requestJSON<EligibilityPolicy>(`/api/v1/commerce/admin/eligibility-policies/${encodeURIComponent(policyId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deleteEligibilityPolicy(policyId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/eligibility-policies/${encodeURIComponent(policyId)}`, {
    method: 'DELETE',
  });
}

export async function listMarkets({
  catalogOwnerTenantId,
  ...filters
}: CatalogOwnerTenantListFilters): Promise<PaginatedList<Market>> {
  const response = await requestJSON<PaginatedListResponse<Market> | null>(
    `/api/v1/commerce/admin/markets${catalogOwnerTenantFilterQuery({
      catalogOwnerTenantId,
      ...filters,
    })}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function listServiceabilityZones({
  catalogOwnerTenantId,
  ...filters
}: CatalogOwnerTenantListFilters): Promise<PaginatedList<ServiceabilityZone>> {
  const response = await requestJSON<PaginatedListResponse<ServiceabilityZone> | null>(
    `/api/v1/commerce/admin/serviceability-zones${catalogOwnerTenantFilterQuery({
      catalogOwnerTenantId,
      ...filters,
    })}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getServiceabilityZone(zoneId: string): Promise<ServiceabilityZone> {
  return requestJSON<ServiceabilityZone>(
    `/api/v1/commerce/admin/serviceability-zones/${encodeURIComponent(zoneId)}`
  );
}

export async function createServiceabilityZone(payload: Record<string, unknown>): Promise<ServiceabilityZone> {
  return requestJSON<ServiceabilityZone>('/api/v1/commerce/admin/serviceability-zones', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateServiceabilityZone(
  zoneId: string,
  payload: Record<string, unknown>
): Promise<ServiceabilityZone> {
  return requestJSON<ServiceabilityZone>(`/api/v1/commerce/admin/serviceability-zones/${encodeURIComponent(zoneId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function listAccessPointLayers({
  catalogOwnerTenantId,
  ...filters
}: CatalogOwnerTenantListFilters): Promise<PaginatedList<AccessPointLayer>> {
  const response = await requestJSON<PaginatedListResponse<AccessPointLayer> | null>(
    `/api/v1/commerce/admin/access-point-layers${catalogOwnerTenantFilterQuery({
      catalogOwnerTenantId,
      ...filters,
    })}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getAccessPointLayer(layerId: string, catalogOwnerTenantId: string): Promise<AccessPointLayer> {
  return requestJSON<AccessPointLayer>(
    `/api/v1/commerce/admin/access-point-layers/${encodeURIComponent(layerId)}${catalogOwnerTenantQuery(catalogOwnerTenantId)}`
  );
}

export async function createAccessPointLayer(payload: Record<string, unknown>): Promise<AccessPointLayer> {
  return requestJSON<AccessPointLayer>('/api/v1/commerce/admin/access-point-layers', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateAccessPointLayer(layerId: string, payload: Record<string, unknown>): Promise<AccessPointLayer> {
  return requestJSON<AccessPointLayer>(`/api/v1/commerce/admin/access-point-layers/${encodeURIComponent(layerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function listAccessPoints(layerId: string, catalogOwnerTenantId: string): Promise<AccessPoint[]> {
  return requestJSON<AccessPoint[]>(
    `/api/v1/commerce/admin/access-point-layers/${encodeURIComponent(layerId)}/access-points${catalogOwnerTenantQuery(catalogOwnerTenantId)}`
  );
}

export async function replaceAccessPoints(layerId: string, payload: Record<string, unknown>): Promise<ReplaceAccessPointsResult> {
  return requestJSON<ReplaceAccessPointsResult>(
    `/api/v1/commerce/admin/access-point-layers/${encodeURIComponent(layerId)}/access-points:replace`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}

export async function getMarket(marketId: string): Promise<Market> {
  return requestJSON<Market>(
    `/api/v1/commerce/admin/markets/${encodeURIComponent(marketId)}`
  );
}

export async function createMarket(payload: Record<string, unknown>): Promise<Market> {
  return requestJSON<Market>('/api/v1/commerce/admin/markets', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateMarket(marketId: string, payload: Record<string, unknown>): Promise<Market> {
  return requestJSON<Market>(`/api/v1/commerce/admin/markets/${encodeURIComponent(marketId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function listMarketGroups({
  catalogOwnerTenantId,
  ...filters
}: CatalogOwnerTenantListFilters): Promise<PaginatedList<MarketGroup>> {
  const response = await requestJSON<PaginatedListResponse<MarketGroup> | null>(
    `/api/v1/commerce/admin/market-groups${catalogOwnerTenantFilterQuery({
      catalogOwnerTenantId,
      ...filters,
    })}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getMarketGroup(marketGroupId: string): Promise<MarketGroup> {
  return requestJSON<MarketGroup>(
    `/api/v1/commerce/admin/market-groups/${encodeURIComponent(marketGroupId)}`
  );
}

export async function createMarketGroup(payload: Record<string, unknown>): Promise<MarketGroup> {
  return requestJSON<MarketGroup>('/api/v1/commerce/admin/market-groups', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updateMarketGroup(marketGroupId: string, payload: Record<string, unknown>): Promise<MarketGroup> {
  return requestJSON<MarketGroup>(`/api/v1/commerce/admin/market-groups/${encodeURIComponent(marketGroupId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function listPromotions(
  filters: CommerceRegistryListInput & {
    discount_type?: string[];
    redemption_method?: string[];
  } = {},
): Promise<PaginatedList<Promotion>> {
  const response = await requestJSON<PaginatedListResponse<Promotion> | null>(
    `/api/v1/commerce/admin/promotions${encodeQuery(filters)}`,
    { cache: 'no-store' },
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function createPromotion(payload: Record<string, unknown>): Promise<Promotion> {
  return requestJSON<Promotion>('/api/v1/commerce/admin/promotions', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function updatePromotion(promotionId: string, payload: Record<string, unknown>): Promise<Promotion> {
  return requestJSON<Promotion>(`/api/v1/commerce/admin/promotions/${encodeURIComponent(promotionId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export async function deletePromotion(promotionId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/promotions/${encodeURIComponent(promotionId)}`, {
    method: 'DELETE',
  });
}

export async function listPromotionCodes(promotionId: string): Promise<PromotionCode[]> {
  return requestJSON<PromotionCode[]>(
    `/api/v1/commerce/admin/promotions/${encodeURIComponent(promotionId)}/codes`,
  );
}

export async function createPromotionCode(
  promotionId: string,
  payload: Record<string, unknown>,
): Promise<PromotionCode> {
  return requestJSON<PromotionCode[] | PromotionCode>(
    `/api/v1/commerce/admin/promotions/${encodeURIComponent(promotionId)}/codes`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  ).then((response) => (Array.isArray(response) ? response[0] : response));
}

export type CartWorkspace = 'catalog-owner' | 'reseller';

function cartWorkspaceBasePath(workspace: CartWorkspace): string {
  return workspace === 'catalog-owner' ? '/api/v1/commerce/admin/carts' : '/api/v1/reseller/carts';
}

export async function listCarts(
  workspace: CartWorkspace,
  pagination: CommerceRegistryListInput = {},
) : Promise<PaginatedList<Cart>> {
  const response = await requestJSON<PaginatedListResponse<Cart> | null>(
    `${cartWorkspaceBasePath(workspace)}${encodeQuery(pagination)}`
  );
  return normalizePaginatedListResponse(response, pagination);
}

export async function listAdminResellerEntitlements(
  filters: CommerceRegistryListInput = {},
): Promise<PaginatedList<AdminResellerEntitlement>> {
  const response = await requestJSON<PaginatedListResponse<AdminResellerEntitlement> | null>(
    `/api/v1/commerce/admin/reseller-entitlements${encodeQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function createAdminResellerEntitlement(
  payload: AdminResellerEntitlementInput,
): Promise<AdminResellerEntitlement> {
  return requestJSON<AdminResellerEntitlement>('/api/v1/commerce/admin/reseller-entitlements', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function getAdminResellerEntitlement(entitlementId: string): Promise<AdminResellerEntitlement> {
  return requestJSON<AdminResellerEntitlement>(
    `/api/v1/commerce/admin/reseller-entitlements/${encodeURIComponent(entitlementId)}`,
  );
}

export async function updateAdminResellerEntitlement(
  entitlementId: string,
  payload: UpdateAdminResellerEntitlementInput,
): Promise<AdminResellerEntitlement> {
  return requestJSON<AdminResellerEntitlement>(
    `/api/v1/commerce/admin/reseller-entitlements/${encodeURIComponent(entitlementId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    },
  );
}

export async function listResellerEntitlements(
  filters: CommerceRegistryListInput = {},
): Promise<PaginatedList<ResellerEntitlement>> {
  const response = await requestJSON<PaginatedListResponse<ResellerEntitlement> | null>(
    `/api/v1/commerce/reseller-entitlements${encodeQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function listAdminCommissionLedger(
  filters: Record<string, string | number | undefined | null> = {}
): Promise<PaginatedList<CommissionLedgerEntry>> {
  const response = await requestJSON<PaginatedListResponse<CommissionLedgerEntry> | null>(
    `/api/v1/commerce/admin/commission-ledger${encodeQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getAdminCommissionLedgerEntry(entryId: string): Promise<CommissionLedgerEntry> {
  return requestJSON<CommissionLedgerEntry>(
    `/api/v1/commerce/admin/commission-ledger/${encodeURIComponent(entryId)}`
  );
}

export type CommissionLedgerWorkspace = 'catalog-owner' | 'reseller';

function commissionLedgerWorkspaceBasePath(workspace: CommissionLedgerWorkspace): string {
  return workspace === 'catalog-owner'
    ? '/api/v1/commerce/admin/commission-ledger'
    : '/api/v1/commerce/commissions/ledger';
}

export async function listCommissionLedger(
  workspace: CommissionLedgerWorkspace,
  filters: CommerceRegistryListInput = {}
): Promise<PaginatedList<CommissionLedgerEntry>> {
  const response = await requestJSON<PaginatedListResponse<CommissionLedgerEntry> | null>(
    `${commissionLedgerWorkspaceBasePath(workspace)}${encodeQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function getCommissionLedgerEntry(
  workspace: CommissionLedgerWorkspace,
  entryId: string
): Promise<CommissionLedgerEntry> {
  return requestJSON<CommissionLedgerEntry>(
    `${commissionLedgerWorkspaceBasePath(workspace)}/${encodeURIComponent(entryId)}`
  );
}

export interface ApproveCommissionProposalInput {
  catalog_owner_tenant_id: string;
  order_id: string;
  commission_plan_id?: string | null;
  actor_user_id?: string | null;
  actor_type?: string | null;
  source?: string | null;
  comment?: string | null;
  metadata?: Record<string, unknown>;
}

export interface RejectCommissionProposalInput {
  catalog_owner_tenant_id: string;
  actor_user_id?: string | null;
  actor_type?: string | null;
  source?: string | null;
  comment?: string | null;
  metadata?: Record<string, unknown>;
}

export async function approveCommissionProposal(input: ApproveCommissionProposalInput): Promise<CommissionLedgerEntry> {
  return requestJSON<CommissionLedgerEntry>('/api/v1/commerce/admin/commission-ledger', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function rejectCommissionProposal(orderId: string, input: RejectCommissionProposalInput): Promise<Order> {
  return requestJSON<Order>(`/api/v1/commerce/admin/commission-ledger/orders/${encodeURIComponent(orderId)}/reject`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function getResellerCommissionSummary(): Promise<ResellerCommissionSummary> {
  return requestJSON<ResellerCommissionSummary>('/api/v1/commerce/reports/resellers/summary');
}

export interface Order {
  order_id: string;
  friendly_order_id: string;
  source_quote_id?: string | null;
  tenant_id: string;
  catalog_owner_tenant_id: string;
  selling_tenant_id: string;
  store_id?: string | null;
  submitted_by_user_id?: string | null;
  submitted_by_email?: string | null;
  assigned_agent_user_id?: string | null;
  status: string;
  lifecycle_phase: string;
  customer_snapshot?: Record<string, unknown>;
  service_address_snapshot?: Record<string, unknown>;
  billing_address_snapshot?: Record<string, unknown>;
  line_items_snapshot?: unknown[];
  pricing_snapshot?: Record<string, unknown>;
  discounts_snapshot?: Record<string, unknown>;
  eligibility_snapshot?: Record<string, unknown>;
  publication_id?: string | null;
  pricebook_id?: string | null;
  commission_plan_id?: string | null;
  commission_snapshot?: Record<string, unknown>;
  source_system: string;
  source_legacy_order_id?: string | null;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  submitted_at?: string | null;
  confirmed_at?: string | null;
}

export class CommerceRequestError extends Error {
  status: number;
  responseText: string;

  constructor(status: number, message: string, responseText = '') {
    super(message);
    this.name = 'CommerceRequestError';
    this.status = status;
    this.responseText = responseText;
  }
}

function parseCommerceErrorMessage(responseText: string, status: number): string {
  const trimmed = responseText.trim();
  if (!trimmed) {
    return `Request failed: ${status}`;
  }

  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    const candidates = [parsed.error, parsed.title, parsed.message, parsed.detail];
    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }
  } catch {
    // Fall through to the raw response body.
  }

  return trimmed;
}

async function requestJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new CommerceRequestError(response.status, parseCommerceErrorMessage(text, response.status), text);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function getCommerceErrorStatus(error: unknown): number | null {
  if (error instanceof CommerceRequestError) {
    return error.status;
  }
  return null;
}

export function getCommerceErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof CommerceRequestError) {
    return error.message || fallback;
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export function buildEligibilityPolicyTargetLabel(
  policy: EligibilityPolicy,
  lookups: {
    product: Map<string, CommerceSelectOptionViewModel>;
    variant: Map<string, CommerceSelectOptionViewModel>;
    serviceClass: Map<string, CommerceSelectOptionViewModel>;
    serviceGroup: Map<string, CommerceSelectOptionViewModel>;
  },
): string {
  const subjectType = policy.subject_type.trim().toLowerCase();
  switch (subjectType) {
    case '':
    case 'all':
    case 'catalog':
      return policy.publication_label?.trim() || 'Catalog / Publication Wide';
    case 'product':
      return lookupOptionLabel(lookups.product, policy.subject_id) || policy.subject_id || 'Selected product';
    case 'product_key':
      return policy.subject_key || 'Selected product key';
    case 'variant':
    case 'product_variant':
      return lookupOptionLabel(lookups.variant, policy.subject_id) || policy.subject_id || 'Selected variant';
    case 'variant_key':
      return policy.subject_key || 'Selected variant key';
    case 'service_class':
      return lookupOptionLabel(lookups.serviceClass, policy.subject_key) || policy.subject_key || 'Selected service class';
    case 'service_group':
      return lookupOptionLabel(lookups.serviceGroup, policy.subject_key) || policy.subject_key || 'Selected service group';
    case 'product_type':
      return policy.subject_key || policy.subject_id || 'Selected product type';
    default:
      return policy.subject_key || policy.subject_id || 'Custom target';
  }
}

export function buildEligibilityPolicyDisplayTitle(
  policy: EligibilityPolicy,
  lookups: {
    product: Map<string, CommerceSelectOptionViewModel>;
    variant: Map<string, CommerceSelectOptionViewModel>;
    serviceClass: Map<string, CommerceSelectOptionViewModel>;
    serviceGroup: Map<string, CommerceSelectOptionViewModel>;
  },
): string {
  return policy.policy_label?.trim() || buildEligibilityPolicyTargetLabel(policy, lookups);
}

export function buildEligibilityPolicyScopeSummary(
  policy: EligibilityPolicy,
  lookups: {
    market: Map<string, CommerceSelectOptionViewModel>;
    segment: Map<string, CommerceSelectOptionViewModel>;
    tenant: Map<string, CommerceSelectOptionViewModel>;
    store: Map<string, CommerceSelectOptionViewModel>;
    agent: Map<string, CommerceSelectOptionViewModel>;
  },
): string {
  const parts: string[] = [];
  if (policy.publication_label?.trim()) {
    parts.push(`Publication ${policy.publication_label.trim()}`);
  }
  if (policy.market_id) {
    parts.push(`Market ${lookupOptionLabel(lookups.market, policy.market_id) || policy.market_id}`);
  }
  if (policy.market_group_id) {
    parts.push(`Segment ${lookupOptionLabel(lookups.segment, policy.market_group_id) || policy.market_group_id}`);
  }
  if (policy.selling_tenant_id) {
    parts.push(`Tenant ${lookupOptionLabel(lookups.tenant, policy.selling_tenant_id) || policy.selling_tenant_id}`);
  }
  if (policy.store_id) {
    parts.push(`Store ${lookupOptionLabel(lookups.store, policy.store_id) || policy.store_id}`);
  }
  if (policy.agent_user_id) {
    parts.push(`Agent ${lookupOptionLabel(lookups.agent, policy.agent_user_id) || policy.agent_user_id}`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'Owner-wide scope';
}

export function formatEligibilitySubjectType(value: string): string {
  switch (value.trim().toLowerCase()) {
    case '':
    case 'all':
    case 'catalog':
      return 'Catalog';
    case 'product':
      return 'Product';
    case 'product_key':
      return 'Product Key';
    case 'variant':
    case 'product_variant':
      return 'Variant';
    case 'variant_key':
      return 'Variant Key';
    case 'product_type':
      return 'Product Type';
    case 'service_class':
      return 'Service Class';
    case 'service_group':
      return 'Service Group';
    default:
      return value.replace(/_/g, ' ').trim() || 'Policy';
  }
}

export function createEmptyEligibilityForm(publicationId = ''): EligibilityFormState {
  return {
    policyLabel: '',
    publicationId,
    subjectType: 'catalog',
    subjectId: '',
    subjectKey: '',
    marketId: '',
    marketGroupId: '',
    sellingTenantId: '',
    storeId: '',
    agentUserId: '',
    action: 'allow',
    requiresCanSignUp: false,
    requiresCanScheduleInstall: false,
    startsAt: '',
    endsAt: '',
    priority: '100',
    metadataJSON: formatJSONObject({}),
  };
}

export function buildEligibilityFormFromPolicy(policy: EligibilityPolicy): EligibilityFormState {
  return {
    policyLabel: policy.policy_label || '',
    publicationId: policy.publication_id || '',
    subjectType: policy.subject_type || 'catalog',
    subjectId: policy.subject_id || '',
    subjectKey: policy.subject_key || '',
    marketId: policy.market_id || '',
    marketGroupId: policy.market_group_id || '',
    sellingTenantId: policy.selling_tenant_id || '',
    storeId: policy.store_id || '',
    agentUserId: policy.agent_user_id || '',
    action: policy.action || 'allow',
    requiresCanSignUp: Boolean(policy.requires_can_sign_up),
    requiresCanScheduleInstall: Boolean(policy.requires_can_schedule_install),
    startsAt: toDatetimeLocal(policy.starts_at),
    endsAt: toDatetimeLocal(policy.ends_at),
    priority: `${policy.priority ?? 100}`,
    metadataJSON: formatJSONObject(policy.metadata),
  };
}

export function buildEligibilityPayload(form: EligibilityFormState, publicationId: string): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    publication_id: publicationId,
    subject_type: form.subjectType.trim(),
    action: form.action,
    requires_can_sign_up: form.requiresCanSignUp,
    requires_can_schedule_install: form.requiresCanScheduleInstall,
    priority: parsePriority(form.priority),
    metadata: parseJSONObject(form.metadataJSON, 'Metadata'),
  };

  const optionalFields: Array<[string, string]> = [
    ['policy_label', form.policyLabel],
    ['subject_id', form.subjectId],
    ['subject_key', form.subjectKey],
    ['market_id', form.marketId],
    ['market_group_id', form.marketGroupId],
    ['selling_tenant_id', form.sellingTenantId],
    ['store_id', form.storeId],
    ['agent_user_id', form.agentUserId],
  ];
  for (const [key, value] of optionalFields) {
    const trimmed = value.trim();
    if (trimmed) {
      payload[key] = trimmed;
    }
  }

  const startsAt = toISOString(form.startsAt, 'Start time');
  if (startsAt) {
    payload.starts_at = startsAt;
  }
  const endsAt = toISOString(form.endsAt, 'End time');
  if (endsAt) {
    payload.ends_at = endsAt;
  }

  return payload;
}

export function toDatetimeLocal(value?: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function toISOString(value: string, label: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`${label} must be a valid date/time.`);
  }
  return date.toISOString();
}

export function formatDate(value?: string | null): string {
  if (!value) {
    return 'Not recorded';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

export function describeEligibilitySubjectType(subjectType: string): string {
  switch (subjectType.trim().toLowerCase()) {
    case 'catalog':
      return 'Applies across all matching products and variants in this storefront publication scope.';
    case 'product':
      return 'Applies to every variant under the selected product.';
    case 'variant':
    case 'product_variant':
      return 'Applies only to the selected variant. Other variants under the same product can still follow different rules.';
    case 'service_class':
      return 'Applies to products and variants that match the selected service class.';
    case 'service_group':
      return 'Applies to products and variants that match the selected service group.';
    default:
      return 'Use a custom subject only when the standard product and variant options do not fit.';
  }
}

export function describeEligibilitySubjectSelection(subjectType: string): string {
  switch (subjectType.trim().toLowerCase()) {
    case 'catalog':
      return 'Use this when the same rule should cover the full storefront publication or commercial scope.';
    case 'product':
      return 'Selecting a product covers all of its variants.';
    case 'variant':
    case 'product_variant':
      return 'Selecting one variant does not automatically change the other variants on the same product.';
    case 'service_class':
      return 'Choose the service class that should share the same sales rule.';
    case 'service_group':
      return 'Choose the service group that should share the same sales rule.';
    default:
      return 'Enter a custom subject only for advanced cases.';
  }
}

export function parsePriority(value: string): number {
  const parsed = Number.parseInt(value.trim() || '100', 10);
  if (!Number.isFinite(parsed)) {
    throw new Error('Priority must be a whole number.');
  }
  return parsed;
}

export function formatJSONObject(value: Record<string, unknown> | undefined): string {
  return JSON.stringify(value || {}, null, 2);
}

export function parseJSONObject(value: string, label: string): Record<string, unknown> {
  const trimmed = value.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error(`${label} must be a JSON object.`);
    }
    return parsed as Record<string, unknown>;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`${label}: ${error.message}`);
    }
    throw new Error(`${label}: invalid JSON.`);
  }
}

export function lookupOptionLabel(
  lookup: Map<string, CommerceSelectOptionViewModel>,
  value?: string | null,
): string | null {
  if (!value) {
    return null;
  }
  return lookup.get(value)?.label || null;
}

export type EligibilityFormState = {
  policyLabel: string;
  publicationId: string;
  subjectType: string;
  subjectId: string;
  subjectKey: string;
  marketId: string;
  marketGroupId: string;
  sellingTenantId: string;
  storeId: string;
  agentUserId: string;
  action: string;
  requiresCanSignUp: boolean;
  requiresCanScheduleInstall: boolean;
  startsAt: string;
  endsAt: string;
  priority: string;
  metadataJSON: string;
};

type QueryValue = string | number | boolean | readonly string[] | undefined | null;

function encodeQuery(values: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        const normalized = item.trim();
        if (normalized) params.append(key, normalized);
      });
      return;
    }
    if (value === undefined || value === null || `${value}`.trim() === '') {
      return;
    }
    params.set(key, `${value}`);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
}

export async function listPublications(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput & { scope?: string[] },
): Promise<PaginatedList<CatalogPublication>> {
  const response = await requestJSON<PaginatedListResponse<CatalogPublication> | null>(
    `/api/v1/commerce/admin/catalog-publications${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export async function listStorefrontExperiences(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput & { segment?: string[] },
): Promise<PaginatedList<StorefrontExperience>> {
  const response = await requestJSON<PaginatedListResponse<StorefrontExperience> | null>(
    `/api/v1/commerce/admin/storefront-experiences${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export function createStorefrontExperience(payload: Record<string, unknown>): Promise<StorefrontExperience> {
  return requestJSON<StorefrontExperience>('/api/v1/commerce/admin/storefront-experiences', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function getStorefrontExperience(experienceId: string): Promise<StorefrontExperience> {
  return requestJSON<StorefrontExperience>(`/api/v1/commerce/admin/storefront-experiences/${encodeURIComponent(experienceId)}`);
}

export function updateStorefrontExperience(experienceId: string, payload: Record<string, unknown>): Promise<StorefrontExperience> {
  return requestJSON<StorefrontExperience>(`/api/v1/commerce/admin/storefront-experiences/${encodeURIComponent(experienceId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteStorefrontExperience(experienceId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/storefront-experiences/${encodeURIComponent(experienceId)}`, {
    method: 'DELETE',
  });
}

export function validateStorefrontExperience(experienceId: string): Promise<StorefrontExperience> {
  return requestJSON<StorefrontExperience>(`/api/v1/commerce/admin/storefront-experiences/${encodeURIComponent(experienceId)}/validate`, {
    method: 'POST',
  });
}

export function repairStorefrontExperience(experienceId: string, actions: string[]): Promise<StorefrontExperience> {
  return requestJSON<StorefrontExperience>(`/api/v1/commerce/admin/storefront-experiences/${encodeURIComponent(experienceId)}/repair`, {
    method: 'POST',
    body: JSON.stringify({ actions }),
  });
}

export function createPublication(payload: Record<string, unknown>): Promise<CatalogPublication> {
  return requestJSON<CatalogPublication>('/api/v1/commerce/admin/catalog-publications', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updatePublication(publicationId: string, payload: Record<string, unknown>): Promise<CatalogPublication> {
  return requestJSON<CatalogPublication>(`/api/v1/commerce/admin/catalog-publications/${encodeURIComponent(publicationId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deletePublication(publicationId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/catalog-publications/${encodeURIComponent(publicationId)}`, {
    method: 'DELETE',
  });
}

export function getPublicationAccessSet(
  publicationId: string,
  kind: CatalogPublicationAccessKind,
): Promise<CatalogPublicationAccessSet> {
  return requestJSON<CatalogPublicationAccessSet>(
    `/api/v1/commerce/admin/catalog-publications/${encodeURIComponent(publicationId)}/${encodeURIComponent(kind)}`,
  );
}

export function replacePublicationAccessSet(
  publicationId: string,
  kind: CatalogPublicationAccessKind,
  ids: string[],
): Promise<CatalogPublicationAccessSet> {
  return requestJSON<CatalogPublicationAccessSet>(
    `/api/v1/commerce/admin/catalog-publications/${encodeURIComponent(publicationId)}/${encodeURIComponent(kind)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    },
  );
}

export function listCommerceChannels({ catalogOwnerTenantId }: CatalogOwnerTenantFilter): Promise<CommerceChannel[]> {
  return requestJSON<CommerceChannel[]>(
    `/api/v1/commerce/admin/channels${catalogOwnerTenantQuery(catalogOwnerTenantId)}`
  );
}

export function previewEligibility(payload: AdminEligibilityPreviewInput): Promise<AdminEligibilityPreviewResponse> {
  return requestJSON<AdminEligibilityPreviewResponse>('/api/v1/commerce/admin/eligibility-preview', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function createCommerceChannel(payload: Record<string, unknown>): Promise<CommerceChannel> {
  return requestJSON<CommerceChannel>('/api/v1/commerce/admin/channels', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateCommerceChannel(channelId: string, payload: Record<string, unknown>): Promise<CommerceChannel> {
  return requestJSON<CommerceChannel>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteCommerceChannel(channelId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}`, {
    method: 'DELETE',
  });
}

export function listCommerceChannelPrincipals(channelId: string): Promise<CommerceChannelPrincipal[]> {
  return requestJSON<CommerceChannelPrincipal[]>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}/principals`);
}

export function replaceCommerceChannelPrincipals(
  channelId: string,
  principals: Array<{ principal_type: string; principal_id: string }>,
): Promise<CommerceChannelPrincipal[]> {
  return requestJSON<CommerceChannelPrincipal[]>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}/principals`, {
    method: 'PUT',
    body: JSON.stringify({ principals }),
  });
}

export function listCommerceChannelPublications(channelId: string): Promise<CommerceChannelPublication[]> {
  return requestJSON<CommerceChannelPublication[]>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}/publications`);
}

export function replaceCommerceChannelPublications(
  channelId: string,
  publications: Array<{
    publication_id: string;
    binding_role: string;
    status: string;
    priority?: number;
    audience?: 'any' | 'public' | 'agent';
    market_id?: string | null;
    market_group_id?: string | null;
    metadata?: Record<string, unknown>;
  }>,
): Promise<CommerceChannelPublication[]> {
  return requestJSON<CommerceChannelPublication[]>(`/api/v1/commerce/admin/channels/${encodeURIComponent(channelId)}/publications`, {
    method: 'PUT',
    body: JSON.stringify({ publications }),
  });
}

export async function listPricebooks(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput & { catalogId?: string },
): Promise<PaginatedList<Pricebook>> {
  const { catalogId, ...listFilters } = filters;
  const response = await requestJSON<PaginatedListResponse<Pricebook> | null>(
    `/api/v1/commerce/admin/pricebooks${catalogOwnerTenantFilterQuery({
      ...listFilters,
      catalog_id: catalogId,
    })}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export function getPricebook(pricebookId: string): Promise<Pricebook> {
  return requestJSON<Pricebook>(`/api/v1/commerce/admin/pricebooks/${encodeURIComponent(pricebookId)}`);
}

export function createPricebook(payload: Record<string, unknown>): Promise<Pricebook> {
  return requestJSON<Pricebook>('/api/v1/commerce/admin/pricebooks', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updatePricebook(pricebookId: string, payload: Record<string, unknown>): Promise<Pricebook> {
  return requestJSON<Pricebook>(`/api/v1/commerce/admin/pricebooks/${encodeURIComponent(pricebookId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deletePricebook(pricebookId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/pricebooks/${encodeURIComponent(pricebookId)}`, {
    method: 'DELETE',
  });
}

export async function listAssignments(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput,
): Promise<PaginatedList<PricebookAssignment>> {
  const response = await requestJSON<PaginatedListResponse<PricebookAssignment> | null>(
    `/api/v1/commerce/admin/pricebook-assignments${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export function getAssignment(assignmentId: string): Promise<PricebookAssignment> {
  return requestJSON<PricebookAssignment>(`/api/v1/commerce/admin/pricebook-assignments/${encodeURIComponent(assignmentId)}`);
}

export function createAssignment(payload: Record<string, unknown>): Promise<PricebookAssignment> {
  return requestJSON<PricebookAssignment>('/api/v1/commerce/admin/pricebook-assignments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateAssignment(assignmentId: string, payload: Record<string, unknown>): Promise<PricebookAssignment> {
  return requestJSON<PricebookAssignment>(`/api/v1/commerce/admin/pricebook-assignments/${encodeURIComponent(assignmentId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteAssignment(assignmentId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/pricebook-assignments/${encodeURIComponent(assignmentId)}`, {
    method: 'DELETE',
  });
}

export async function listCommissionPlanAssignments(
  filters: CatalogOwnerTenantFilter & CommerceRegistryListInput,
): Promise<PaginatedList<CommissionPlanAssignment>> {
  const response = await requestJSON<PaginatedListResponse<CommissionPlanAssignment> | null>(
    `/api/v1/commerce/admin/commission-plan-assignments${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export function getCommissionPlanAssignment(assignmentId: string): Promise<CommissionPlanAssignment> {
  return requestJSON<CommissionPlanAssignment>(
    `/api/v1/commerce/admin/commission-plan-assignments/${encodeURIComponent(assignmentId)}`
  );
}

export function createCommissionPlanAssignment(payload: Record<string, unknown>): Promise<CommissionPlanAssignment> {
  return requestJSON<CommissionPlanAssignment>('/api/v1/commerce/admin/commission-plan-assignments', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateCommissionPlanAssignment(assignmentId: string, payload: Record<string, unknown>): Promise<CommissionPlanAssignment> {
  return requestJSON<CommissionPlanAssignment>(
    `/api/v1/commerce/admin/commission-plan-assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }
  );
}

export function deleteCommissionPlanAssignment(assignmentId: string): Promise<void> {
  return requestJSON<void>(
    `/api/v1/commerce/admin/commission-plan-assignments/${encodeURIComponent(assignmentId)}`,
    {
      method: 'DELETE',
    }
  );
}

export async function listCatalogs(filters: CatalogListFilters): Promise<PaginatedList<Catalog>> {
  const response = await requestJSON<PaginatedListResponse<Catalog> | null>(
    `/api/v1/commerce/admin/catalogs${catalogOwnerTenantFilterQuery(filters)}`
  );
  return normalizePaginatedListResponse(response, filters);
}

export function getCatalog(catalogId: string): Promise<Catalog> {
  return requestJSON<Catalog>(`/api/v1/commerce/admin/catalogs/${encodeURIComponent(catalogId)}`);
}

export function createCatalog(payload: Record<string, unknown>): Promise<Catalog> {
  return requestJSON<Catalog>('/api/v1/commerce/admin/catalogs', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateCatalog(catalogId: string, payload: Record<string, unknown>): Promise<Catalog> {
  return requestJSON<Catalog>(`/api/v1/commerce/admin/catalogs/${encodeURIComponent(catalogId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveCatalog(catalogId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/catalogs/${encodeURIComponent(catalogId)}`, {
    method: 'DELETE',
  });
}

export async function listCatalogProducts(
  catalogId: string,
  pagination: ListPaginationInput & {
    search?: string;
    sort?: string[];
    include_variants?: boolean;
    include_bundle_components?: boolean;
  } = {},
): Promise<PaginatedList<Product>> {
  const response = await requestJSON<PaginatedListResponse<Product> | null>(
    `/api/v1/commerce/admin/catalogs/${encodeURIComponent(catalogId)}/products${encodeQuery(pagination)}`
  );
  return normalizePaginatedListResponse(response, pagination);
}

export function getProduct(productId: string): Promise<Product> {
  return requestJSON<Product>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}`);
}

export function createProduct(catalogId: string, payload: Record<string, unknown>): Promise<Product> {
  return requestJSON<Product>(`/api/v1/commerce/admin/catalogs/${encodeURIComponent(catalogId)}/products`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateProduct(productId: string, payload: Record<string, unknown>): Promise<Product> {
  return requestJSON<Product>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveProduct(productId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}`, {
    method: 'DELETE',
  });
}

export function createVariant(productId: string, payload: Record<string, unknown>): Promise<ProductVariant> {
  return requestJSON<ProductVariant>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/variants`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function listAttributeDefinitions(catalogOwnerTenantId: string): Promise<CommerceAttributeDefinition[]> {
  return requestJSON<CommerceAttributeDefinition[]>(
    `/api/v1/commerce/admin/attribute-definitions${catalogOwnerTenantQuery(catalogOwnerTenantId)}`
  );
}

export function updateVariant(variantId: string, payload: Record<string, unknown>): Promise<ProductVariant> {
  return requestJSON<ProductVariant>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariant(variantId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}`, {
    method: 'DELETE',
  });
}

export function createVariantOffer(variantId: string, payload: Record<string, unknown>): Promise<ProductVariantOffer> {
  return requestJSON<ProductVariantOffer>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/offers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVariantOffer(offerId: string, payload: Record<string, unknown>): Promise<ProductVariantOffer> {
  return requestJSON<ProductVariantOffer>(`/api/v1/commerce/admin/offers/${encodeURIComponent(offerId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariantOffer(offerId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/offers/${encodeURIComponent(offerId)}`, {
    method: 'DELETE',
  });
}

export function createOption(productId: string, payload: Record<string, unknown>): Promise<ProductOption> {
  return requestJSON<ProductOption>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/options`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateOption(optionId: string, payload: Record<string, unknown>): Promise<ProductOption> {
  return requestJSON<ProductOption>(`/api/v1/commerce/admin/options/${encodeURIComponent(optionId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveOption(optionId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/options/${encodeURIComponent(optionId)}`, {
    method: 'DELETE',
  });
}

export function createOptionValue(optionId: string, payload: Record<string, unknown>): Promise<ProductOptionValue> {
  return requestJSON<ProductOptionValue>(`/api/v1/commerce/admin/options/${encodeURIComponent(optionId)}/values`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateOptionValue(optionValueId: string, payload: Record<string, unknown>): Promise<ProductOptionValue> {
  return requestJSON<ProductOptionValue>(`/api/v1/commerce/admin/option-values/${encodeURIComponent(optionValueId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveOptionValue(optionValueId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/option-values/${encodeURIComponent(optionValueId)}`, {
    method: 'DELETE',
  });
}

export function createProductMedia(productId: string, payload: Record<string, unknown>): Promise<ProductMediaPlacement> {
  return requestJSON<ProductMediaPlacement>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/media`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function initProductMediaUpload(productId: string, payload: ProductMediaUploadInitRequest): Promise<ProductMediaUploadInitResponse> {
  return requestJSON<ProductMediaUploadInitResponse>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/media/uploads/init`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function completeProductMediaUpload(productId: string, payload: ProductMediaUploadCompleteRequest): Promise<ProductMediaPlacement> {
  return requestJSON<ProductMediaPlacement>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/media/uploads/complete`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function uploadProductMediaFile(
  presignedUrl: string,
  file: File,
  options: ProductMediaUploadOptions = {},
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const handleAbort = () => request.abort();

    const cleanup = () => {
      options.signal?.removeEventListener('abort', handleAbort);
    };

    request.open('PUT', presignedUrl);
    request.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    request.upload.addEventListener('progress', event => {
      const totalBytes = event.lengthComputable ? event.total : file.size;
      options.onProgress?.({ loadedBytes: event.loaded, totalBytes });
    });
    request.addEventListener('load', () => {
      cleanup();
      if (request.status >= 200 && request.status < 300) {
        options.onProgress?.({ loadedBytes: file.size, totalBytes: file.size });
        resolve();
        return;
      }
      reject(new CommerceRequestError(request.status, `Upload failed: ${request.status}`));
    });
    request.addEventListener('error', () => {
      cleanup();
      reject(new CommerceRequestError(request.status || 0, 'Upload failed before the object store responded.'));
    });
    request.addEventListener('abort', () => {
      cleanup();
      reject(new DOMException('Upload cancelled', 'AbortError'));
    });

    if (options.signal?.aborted) {
      cleanup();
      reject(new DOMException('Upload cancelled', 'AbortError'));
      return;
    }
    options.signal?.addEventListener('abort', handleAbort, { once: true });
    request.send(file);
  });
}

export function replaceProductMediaOrder(productId: string, placementIds: string[]): Promise<ProductMediaPlacement[]> {
  return requestJSON<ProductMediaPlacement[]>(`/api/v1/commerce/admin/products/${encodeURIComponent(productId)}/media/order`, {
    method: 'PUT',
    body: JSON.stringify({ placement_ids: placementIds }),
  });
}

export function updateProductMedia(placementId: string, payload: Record<string, unknown>): Promise<ProductMediaPlacement> {
  return requestJSON<ProductMediaPlacement>(`/api/v1/commerce/admin/product-media/${encodeURIComponent(placementId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveProductMedia(placementId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/product-media/${encodeURIComponent(placementId)}`, {
    method: 'DELETE',
  });
}

export function createVariantFeature(variantId: string, payload: Record<string, unknown>): Promise<VariantFeature> {
  return requestJSON<VariantFeature>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/features`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVariantFeature(variantId: string, featureId: string, payload: Record<string, unknown>): Promise<VariantFeature> {
  return requestJSON<VariantFeature>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/features/${encodeURIComponent(featureId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariantFeature(variantId: string, featureId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/features/${encodeURIComponent(featureId)}`, {
    method: 'DELETE',
  });
}

export function createVariantFee(variantId: string, payload: Record<string, unknown>): Promise<VariantFee> {
  return requestJSON<VariantFee>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/fees`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVariantFee(variantId: string, feeId: string, payload: Record<string, unknown>): Promise<VariantFee> {
  return requestJSON<VariantFee>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/fees/${encodeURIComponent(feeId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariantFee(variantId: string, feeId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/fees/${encodeURIComponent(feeId)}`, {
    method: 'DELETE',
  });
}

export function createVariantRecommendation(variantId: string, payload: Record<string, unknown>): Promise<VariantRecommendation> {
  return requestJSON<VariantRecommendation>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/recommendations`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVariantRecommendation(variantId: string, recommendationId: string, payload: Record<string, unknown>): Promise<VariantRecommendation> {
  return requestJSON<VariantRecommendation>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/recommendations/${encodeURIComponent(recommendationId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariantRecommendation(variantId: string, recommendationId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/recommendations/${encodeURIComponent(recommendationId)}`, {
    method: 'DELETE',
  });
}

export function createVariantBundleComponent(variantId: string, payload: Record<string, unknown>): Promise<VariantBundleComponent> {
  return requestJSON<VariantBundleComponent>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/bundle-components`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateVariantBundleComponent(variantId: string, bundleComponentId: string, payload: Record<string, unknown>): Promise<VariantBundleComponent> {
  return requestJSON<VariantBundleComponent>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/bundle-components/${encodeURIComponent(bundleComponentId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function archiveVariantBundleComponent(variantId: string, bundleComponentId: string): Promise<void> {
  return requestJSON<void>(`/api/v1/commerce/admin/variants/${encodeURIComponent(variantId)}/bundle-components/${encodeURIComponent(bundleComponentId)}`, {
    method: 'DELETE',
  });
}

function quoteWorkspaceQuery(workspace: QuoteWorkspace, filters: Record<string, QueryValue> = {}) {
  return encodeQuery({
    ...filters,
    workspace,
  });
}

export async function listQuotes(workspace: QuoteWorkspace, pagination: CommerceRegistryListInput = {}): Promise<PaginatedList<Quote>> {
  const response = await requestJSON<PaginatedListResponse<Quote> | null>(`/api/v1/quotes${quoteWorkspaceQuery(workspace, pagination)}`);
  return normalizePaginatedListResponse(response, pagination);
}

export function createQuote(input: CreateSalesQuoteInput): Promise<Quote> {
  return requestJSON<Quote>('/api/v1/quotes', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

function normalizeListResponse<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function normalizePaginatedListResponse<T>(
  value: PaginatedListResponse<T> | null | undefined,
  request: Record<string, QueryValue> = {},
): PaginatedList<T> {
  const requestedLimit = typeof request.limit === 'number' ? request.limit : 0;
  const requestedOffset = typeof request.offset === 'number' ? request.offset : 0;

  return {
    items: Array.isArray(value?.items) ? value.items : [],
    total_count: typeof value?.total_count === 'number' ? Math.max(0, value.total_count) : 0,
    limit: typeof value?.limit === 'number' ? value.limit : requestedLimit,
    offset: typeof value?.offset === 'number' ? value.offset : requestedOffset,
    facets: Object.fromEntries(
      Object.entries(value?.facets ?? {}).map(([key, buckets]) => [key, Array.isArray(buckets) ? buckets : []]),
    ),
  };
}

export function getQuote(quoteId: string, workspace: QuoteWorkspace): Promise<Quote> {
  return requestJSON<Quote>(`/api/v1/quotes/${encodeURIComponent(quoteId)}${quoteWorkspaceQuery(workspace)}`);
}

export function updateQuote(quoteId: string, payload: Record<string, unknown>, workspace: QuoteWorkspace): Promise<Quote> {
  return requestJSON<Quote>(`/api/v1/quotes/${encodeURIComponent(quoteId)}${quoteWorkspaceQuery(workspace)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function listQuoteNotes(quoteId: string, workspace: QuoteWorkspace): Promise<TimelineNote[]> {
  return requestJSON<TimelineNote[] | null>(`/api/v1/quotes/${encodeURIComponent(quoteId)}/notes${quoteWorkspaceQuery(workspace)}`).then(normalizeListResponse);
}

export function addQuoteNote(quoteId: string, body: string, workspace: QuoteWorkspace): Promise<TimelineNote> {
  return requestJSON<TimelineNote>(`/api/v1/quotes/${encodeURIComponent(quoteId)}/notes${quoteWorkspaceQuery(workspace)}`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export function listQuoteEvents(quoteId: string, workspace: QuoteWorkspace): Promise<TimelineEvent[]> {
  return requestJSON<TimelineEvent[] | null>(`/api/v1/quotes/${encodeURIComponent(quoteId)}/events${quoteWorkspaceQuery(workspace)}`).then(normalizeListResponse);
}

export function performQuoteAction(quoteId: string, action: 'submit' | 'approve' | 'reject' | 'cancel', payload: Record<string, unknown> | undefined, workspace: QuoteWorkspace): Promise<Quote> {
  return requestJSON<Quote>(`/api/v1/quotes/${encodeURIComponent(quoteId)}/${action}${quoteWorkspaceQuery(workspace)}`, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });
}

export function convertQuote(quoteId: string, payload: Record<string, unknown> | undefined, workspace: QuoteWorkspace): Promise<Order> {
  return requestJSON<Order>(`/api/v1/quotes/${encodeURIComponent(quoteId)}/convert${quoteWorkspaceQuery(workspace)}`, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });
}

export type OrderWorkspace = 'catalog-owner' | 'reseller';

function orderWorkspaceBasePath(workspace: OrderWorkspace): string {
  return workspace === 'catalog-owner' ? '/api/v1/commerce/orders' : '/api/v1/reseller/orders';
}

export async function listOrders(
  workspace: OrderWorkspace,
  pagination: CommerceRegistryListInput = {},
) : Promise<PaginatedList<Order>> {
  const response = await requestJSON<PaginatedListResponse<Order> | null>(
    `${orderWorkspaceBasePath(workspace)}${encodeQuery(pagination)}`
  );
  return normalizePaginatedListResponse(response, pagination);
}

export function createOrder(input: CreateSalesOrderInput): Promise<Order> {
  return requestJSON<Order>('/api/v1/reseller/orders', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getOrder(workspace: OrderWorkspace, orderId: string): Promise<Order> {
  return requestJSON<Order>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}`);
}

export function listOrderNotes(workspace: OrderWorkspace, orderId: string): Promise<TimelineNote[]> {
  return requestJSON<TimelineNote[] | null>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/notes`).then(normalizeListResponse);
}

export function addOrderNote(workspace: OrderWorkspace, orderId: string, body: string): Promise<TimelineNote> {
  return requestJSON<TimelineNote>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/notes`, {
    method: 'POST',
    body: JSON.stringify({ body }),
  });
}

export function listOrderEvents(workspace: OrderWorkspace, orderId: string): Promise<TimelineEvent[]> {
  return requestJSON<TimelineEvent[] | null>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/events`).then(normalizeListResponse);
}

function normalizeOrderAction(action: OrderActionResponse): OrderAction {
  return {
    ...action,
    blocked_reasons: Array.isArray(action.blocked_reasons) ? action.blocked_reasons : [],
  };
}

export async function listOrderActions(workspace: OrderWorkspace, orderId: string): Promise<OrderAction[]> {
  const actions = await requestJSON<OrderActionResponse[] | null>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/actions`);
  if (!Array.isArray(actions)) {
    return [];
  }
  return actions.map(normalizeOrderAction);
}

export function executeOrderAction(workspace: OrderWorkspace, orderId: string, actionId: string, input?: Record<string, unknown>): Promise<unknown> {
  return requestJSON<unknown>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/actions/${encodeURIComponent(actionId)}`, {
    method: 'POST',
    body: JSON.stringify({ action_input: input ?? {} }),
  });
}

export function resendOrderConfirmation(workspace: OrderWorkspace, orderId: string): Promise<unknown> {
  return requestJSON<unknown>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/confirmations/resend`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function regenerateOrderConfirmation(workspace: OrderWorkspace, orderId: string): Promise<unknown> {
  return requestJSON<unknown>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/confirmations/regenerate`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function updateOrderStatus(workspace: OrderWorkspace, orderId: string, status: string, lifecyclePhase?: string): Promise<Order> {
  return requestJSON<Order>(`${orderWorkspaceBasePath(workspace)}/${encodeURIComponent(orderId)}/status`, {
    method: 'POST',
    body: JSON.stringify({
      status,
      ...(lifecyclePhase ? { lifecycle_phase: lifecyclePhase } : {}),
    }),
  });
}
